import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { permissionsFromToken, rolesFromToken } from '../utils/permissions';

export type PermissionsState = {
  /** True until the access token has been read (or could not be). */
  isLoading: boolean;
  permissions: string[];
  /** The user's Auth0 role names; empty for an account nobody has given a role yet. */
  roles: string[];
  has: (permission: string) => boolean;
  /** Asks Auth0 for a new token, bypassing the cache: a role given since sign-in shows up in it. */
  refresh: () => Promise<void>;
};

type TokenState = {
  isLoading: boolean;
  permissions: string[];
  roles: string[];
};

function fromToken(token: string): TokenState {
  return {
    isLoading: false,
    permissions: permissionsFromToken(token),
    roles: rolesFromToken(token),
  };
}

const NONE: TokenState = { isLoading: false, permissions: [], roles: [] };

/**
 * The signed-in user's API permissions and roles, read from the `permissions` and roles claims of their Auth0
 * access token. A user without a token (or whose token cannot be refreshed) has none.
 */
export function usePermissions(): PermissionsState {
  const {
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const [state, setState] = useState<TokenState>({
    isLoading: true,
    permissions: [],
    roles: [],
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated) {
      setState(NONE);
      return;
    }
    let cancelled = false;
    // The Cypress stub of Auth0 returns the token synchronously; Promise.resolve covers both.
    Promise.resolve()
      .then(() => getAccessTokenSilently())
      .then(
        token => !cancelled && setState(fromToken(token)),
        () => !cancelled && setState(NONE)
      );
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, getAccessTokenSilently]);

  const refresh = useCallback(async () => {
    setState(previous => ({ ...previous, isLoading: true }));
    try {
      setState(fromToken(await getAccessTokenSilently({ cacheMode: 'off' })));
    } catch {
      setState(previous => ({ ...previous, isLoading: false }));
    }
  }, [getAccessTokenSilently]);

  const has = useCallback(
    (permission: string) => state.permissions.includes(permission),
    [state.permissions]
  );

  return { ...state, has, refresh };
}
